import { useMemo } from 'react';

import { OrthographicCamera } from '@react-three/drei';
import { ThreeCanvas } from '@remotion/three';
import { useAtomValue } from 'jotai';

import { cameraConfigAtom } from '@store';

import { useBounds, useOptions } from './context';
import { useSkeletonData } from './useSkeletonData';
import { Bone } from './VideoSkeletonShapes';

export const BALL_POSITION: [number, number, number] = [0.081, 0.355, -0.045];

const CAMERA_NEAR_PLANE = 0.000000001;
const CAMERA_FAR_PLANE = 10000;
const DPR_RANGE: [number, number] = [1, 2];

const CANVAS_STYLE = {
    position: 'absolute',
    top: 0,
    left: 0,
    pointerEvents: 'none',
    background: 'transparent',
} as const;

const GL_OPTIONS = {
    alpha: true,
    antialias: true,
};

export type KeypointPosition = [number, number, number];
export type Frame = Record<string, KeypointPosition>;

interface BoneDescriptor {
    start_point_id: number;
    end_point_id: number;
}

interface SkeletonProps {
    cameraView: number;
}

function resolveBoneEndpoints(bone: number[] | BoneDescriptor): [string | number, string | number] {
    if (Array.isArray(bone)) {
        return [bone[0], bone[1]];
    }
    return [bone.start_point_id, bone.end_point_id];
}

function useScaledDimensions() {
    const bounds = useBounds();
    const { columns, videoCount } = useOptions();
    const cameraConfig = useAtomValue(cameraConfigAtom);

    return useMemo(() => {
        const cameraType = cameraConfig?.camera_types?.at(0);
        const sourceHeight = cameraType?.image_height ?? 0;
        const sourceWidth = cameraType?.image_width ?? 1;

        const cellHeight = Math.floor(videoCount === 1 ? bounds.height : bounds.height / 2);
        const cellWidth = Math.floor(videoCount === 1 ? bounds.width : bounds.width / columns) || 1;

        const offsetY = sourceHeight - cellHeight;
        const offsetX = sourceWidth - cellWidth;

        const cellAspect = cellWidth / cellHeight;
        const sourceAspect = sourceWidth / sourceHeight;

        // When the cell is narrower than the source, scale by height; otherwise by width
        const scale = cellAspect < sourceAspect ? cellHeight / sourceHeight : cellWidth / sourceWidth;

        return { cellWidth, cellHeight, offsetX, offsetY, scale };
    }, [bounds.height, bounds.width, cameraConfig?.camera_types, columns, videoCount]);
}

export function VideoSkeleton({ cameraView }: SkeletonProps) {
    const skeletonData = useSkeletonData(cameraView);
    const dimensions = useScaledDimensions();

    if (!skeletonData) {
        return null;
    }

    const { bones, keypointMap } = skeletonData;
    const { cellWidth, cellHeight, offsetX, offsetY, scale } = dimensions;

    return (
        <ThreeCanvas
            style={CANVAS_STYLE}
            dpr={DPR_RANGE}
            gl={GL_OPTIONS}
            width={cellWidth}
            height={cellHeight}
            shadows={false}
        >
            <OrthographicCamera makeDefault near={CAMERA_NEAR_PLANE} far={CAMERA_FAR_PLANE} zoom={scale} />
            <group
                rotation={[Math.PI, 0, 0]}
                position={[-(cellWidth + offsetX) / 2, (cellHeight + offsetY) / 2, 0]}
                renderOrder={0}
            >
                {bones.map((bone) => {
                    const [startId, endId] = resolveBoneEndpoints(bone);
                    return (
                        <Bone
                            key={`bone_${startId}_${endId}`}
                            startPoint={keypointMap[startId]}
                            endPoint={keypointMap[endId]}
                            startPointId={startId}
                            endPointId={endId}
                        />
                    );
                })}
            </group>
        </ThreeCanvas>
    );
}
